import { useState, useMemo, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { ClockIcon } from '@phosphor-icons/react';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

import {
  MOCK_INVOICES,
  MOCK_CUSTOMERS,
  type ARInvoice,
} from '../data/mockARData';
import { InvoiceDetailModal } from './components/InvoiceDetailModal';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'partial', label: 'Partial' },
  { id: 'paid', label: 'Paid' },
  { id: 'overdue', label: 'Overdue' },
];

const CUSTOMER_OPTIONS: MultiSelectOption[] = MOCK_CUSTOMERS.map((c) => ({
  id: c.id,
  label: c.name,
}));

function StatusBadge({ status }: { status: ARInvoice['status'] }) {
  const variants: Record<
    ARInvoice['status'],
    { className: string; label: string }
  > = {
    unpaid: {
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      label: 'Unpaid',
    },
    partial: {
      className: 'bg-blue-100 text-blue-700 border-blue-200',
      label: 'Partial',
    },
    paid: {
      className: 'bg-green-100 text-green-700 border-green-200',
      label: 'Paid',
    },
    overdue: {
      className: 'bg-red-100 text-red-700 border-red-200',
      label: 'Overdue',
    },
  };
  const variant = variants[status];
  return <Badge className={variant.className}>{variant.label}</Badge>;
}

export function ARInvoicesPage() {
  const [selectedInvoice, setSelectedInvoice] = useState<ARInvoice | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'dueDate', desc: false },
  ]);

  // Filter states
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string[]>([]);

  const handleRowClick = (invoice: ARInvoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const filteredData = useMemo(() => {
    return MOCK_INVOICES.filter((invoice) => {
      // Invoice number filter
      if (
        invoiceNumberFilter &&
        !invoice.invoiceNumber
          .toLowerCase()
          .includes(invoiceNumberFilter.toLowerCase())
      ) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0 && !statusFilter.includes(invoice.status)) {
        return false;
      }

      // Customer filter
      if (
        customerFilter.length > 0 &&
        !customerFilter.includes(invoice.customerId)
      ) {
        return false;
      }

      return true;
    });
  }, [invoiceNumberFilter, statusFilter, customerFilter]);

  const columns: ColumnDef<ARInvoice>[] = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice #',
        cell: ({ getValue }) => (
          <span className="font-medium text-sm">{getValue<string>()}</span>
        ),
        size: 140,
      },
      {
        accessorKey: 'customerName',
        header: 'Customer',
        cell: ({ row }) => (
          <div className="max-w-[200px]">
            <Link
              to="/ar/collections/$customerId"
              params={{ customerId: row.original.customerId }}
              className="text-sm font-medium text-foreground hover:text-blue-600 hover:underline truncate block"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.customerName}
            </Link>
          </div>
        ),
      },
      {
        accessorKey: 'issueDate',
        header: 'Issue Date',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(getValue<string>())}
          </span>
        ),
        size: 120,
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        cell: ({ getValue, row }) => {
          const dateStr = getValue<string>();
          const dueDate = new Date(dateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDue =
            dueDate.getTime() < today.getTime() &&
            row.original.status !== 'paid';

          return (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {formatDate(dateStr)}
              </span>
              {isPastDue && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ClockIcon className="h-4 w-4 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Past Due</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ getValue }) => (
          <span className="font-semibold text-sm">
            {formatCurrency(getValue<number>())}
          </span>
        ),
        size: 120,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => (
          <StatusBadge status={getValue<ARInvoice['status']>()} />
        ),
        size: 100,
      },
      {
        accessorKey: 'paymentTerms',
        header: 'Terms',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">
            {getValue<string>()}
          </span>
        ),
        size: 80,
      },
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data: filteredData,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: { pageSize: 25 },
    },
  });

  const handleClearFilters = useCallback(() => {
    setInvoiceNumberFilter('');
    setStatusFilter([]);
    setCustomerFilter([]);
  }, []);

  const isFiltered = useMemo(() => {
    return Boolean(
      invoiceNumberFilter ||
        statusFilter.length > 0 ||
        customerFilter.length > 0,
    );
  }, [invoiceNumberFilter, statusFilter, customerFilter]);

  const handlePageChange = useCallback(
    (pageIndex: number) => {
      table.setPageIndex(pageIndex);
    },
    [table],
  );

  return (
    <Page title="AR Invoices">
      <div className="space-y-4">
        <Card className="p-4 space-y-4">
          <DataTableToolbar
            table={table}
            onClearFilters={handleClearFilters}
            hasCustomFilters={isFiltered}
          >
            <Input
              placeholder="Invoice #"
              value={invoiceNumberFilter}
              onChange={(e) => setInvoiceNumberFilter(e.target.value)}
              className="h-8 w-[140px]"
            />
            <MultiSelect
              options={CUSTOMER_OPTIONS}
              value={CUSTOMER_OPTIONS.filter((o) =>
                customerFilter.includes(o.id),
              )}
              onChange={(options) =>
                setCustomerFilter(options.map((o) => o.id))
              }
              placeholder="Customer"
              size="sm"
              className="h-8 w-[200px]"
            />
            <MultiSelect
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.filter((o) => statusFilter.includes(o.id))}
              onChange={(options) => setStatusFilter(options.map((o) => o.id))}
              placeholder="Status"
              size="sm"
              shouldAutoSort={false}
              className="h-8"
            />
          </DataTableToolbar>
        </Card>

        <Card>
          <DataTable table={table} onRowClick={handleRowClick} />
          <DataTablePagination
            table={table}
            pageCount={table.getPageCount()}
            pageIndex={table.getState().pagination.pageIndex}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      <InvoiceDetailModal
        invoice={selectedInvoice}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </Page>
  );
}

export default ARInvoicesPage;
